import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import AssignmentDialog from "@/components/AssignmentDialog";
import GradeRosterDialog from "@/components/GradeRosterDialog";
import { TYPE_LABEL } from "@/lib/constants";
import {
  ArrowLeft, Plus, Loader2, Sparkles, CalendarClock, ClipboardList, PencilLine, Calculator, BookOpen, Leaf, Palette, Trash2,
} from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toast } from "sonner";

const ICONS = { BookOpen, Calculator, Leaf, Palette };

export default function SubjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [subject, setSubject] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [gradeFor, setGradeFor] = useState(null);
  const [delFor, setDelFor] = useState(null);

  const remove = async () => {
    try {
      await api.delete(`/assignments/${delFor.id}`);
      toast.success("Tugas dihapus");
      setDelFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus tugas");
    }
  };

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/subjects"), api.get("/assignments", { params: { subject_id: id } })]).then(([s, a]) => {
      setSubjects(s.data);
      setSubject(s.data.find((x) => x.id === id) || null);
      setAssignments(a.data);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  if (!subject) return <div className="text-center py-20 text-slate-400">Mata pelajaran tidak ditemukan.</div>;

  const Icon = ICONS[subject.icon] || BookOpen;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <button onClick={() => navigate("/guru/mapel")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="back-to-subjects">
        <ArrowLeft className="w-4 h-4" /> Kembali ke daftar mapel
      </button>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm flex items-start gap-4">
        <div className="w-14 h-14 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center shrink-0"><Icon className="w-7 h-7" /></div>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-slate-800">{subject.name}</h1>
          <p className="text-slate-500 mt-1">{subject.description}</p>
          <div className="flex flex-wrap gap-1 mt-2">
            {(subject.jenjang || []).map((k) => <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Kelas {k}</span>)}
          </div>
          {subject.schedule && <p className="text-xs text-slate-400 mt-2">Jadwal: {subject.schedule}</p>}
          {subject.materials && <p className="text-sm text-slate-600 mt-3 bg-slate-50 rounded-xl p-3">{subject.materials}</p>}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-slate-800">Tugas dalam Mapel Ini ({assignments.length})</h2>
        <Button onClick={() => setOpen(true)} className="rounded-xl bg-teal-600 hover:bg-teal-700" data-testid="add-assignment-in-subject-btn">
          <Plus className="w-5 h-5 mr-2" /> Buat Tugas
        </Button>
      </div>

      {assignments.length === 0 ? (
        <div className="text-center py-16 text-slate-400 bg-white rounded-2xl border border-slate-100">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Belum ada tugas untuk mapel ini. Buat tugas pertama!</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4 stagger">
          {assignments.map((a) => (
            <div key={a.id} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid={`subject-assignment-${a.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-medium">{TYPE_LABEL[a.type]}</span>
                {a.ai_enabled && <span className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium"><Sparkles className="w-3 h-3" /> AI</span>}
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-800 mt-3">{a.title}</h3>
              <p className="text-sm text-slate-500 mt-1">Kelas {a.kelas}</p>
              {a.due_date && <p className="text-xs text-orange-600 mt-2 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Tenggat: {a.due_date.slice(0, 10)}</p>}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" className="rounded-xl flex-1" onClick={() => setGradeFor(a)} data-testid={`input-nilai-${a.id}`}>
                  <PencilLine className="w-4 h-4 mr-1.5" /> Input Nilai
                </Button>
                <Button variant="outline" size="sm" className="rounded-xl text-rose-600 px-2.5" onClick={() => setDelFor(a)} data-testid={`delete-assignment-${a.id}`} aria-label="Hapus tugas">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AssignmentDialog open={open} onOpenChange={setOpen} subjects={subjects} lockedSubjectId={id} defaultKelas={(subject.jenjang || [])[0]} onCreated={load} />
      <GradeRosterDialog open={!!gradeFor} onOpenChange={(v) => !v && setGradeFor(null)} assignment={gradeFor} />
      <ConfirmDialog open={!!delFor} onOpenChange={(v) => !v && setDelFor(null)} title={`Hapus tugas "${delFor?.title}"?`}
        description="Semua pengumpulan dan nilai murid untuk tugas ini akan ikut terhapus." onConfirm={remove} testId="delete-assignment-dialog" />
    </div>
  );
}
